import React, { useState } from 'react'
import * as id from '../reportId'
import { Item } from '../../../common/snippets'
import * as sx from '../../../common/stylesInline/report_daily_branch'
import moment from 'moment'
import { Box, Button } from '@mui/material'
import Datepicker from '../../../common/datePicker'
import DropDownOA from '../../../common/dropDownOA'
import { ReportHeading } from '../common/ReportHeading'
import { openReportInNewTab } from '../DispatchReport/DispatchReportUtils'
import { vehicleWiseVoucherColumns, vehicleWiseVouchersummaryColumns } from './Constant'
import { useSetAtom } from 'jotai'
import { alertAtom, globalLoaderAtom } from '../../../common/jotaiState/globalStateAtoms'
import { urls } from '../../../common/constants'
import { GetRequest } from '../../../common/api'
import useToken from '../../../helpers/useTokens'

const toNumber = (value) => Number(value) || 0
const round2 = (value) => Number(toNumber(value).toFixed(2))

const VehicleWiseVoucher = () => {
  const { loginData } = useToken()
  const setAlert = useSetAtom(alertAtom)
  const setLoader = useSetAtom(globalLoaderAtom)
  const reportName = 'Vehicle Wise Voucher'
  const [fromDate, setFromDate] = useState(moment(new Date()))
  const [toDate, setToDate] = useState(moment(new Date()))
  const [vehicleData, setVehicleData] = useState([])
  const [selectedVehicle, setSelectedVehicle] = useState('')
  const goButtonId = 'vehicleWiseVoucherGoBtn'
  const loadButtonId = 'vehicleWiseVoucherLoadBtn'

  const getRequestBody = (vehicleNo = "''") => ({
    CompanyID: loginData?.intCompanyID,
    DateFrom: moment(fromDate).format('YYYY-MM-DD'),
    DateTo: moment(toDate).format('YYYY-MM-DD'),
    VehicleNo: vehicleNo,
  })

  const criteriaHeaders = [
    { headerName: 'From Dispatch Date', header: 'From Dispatch Date', exportHead: 'From Dispatch Date', field: 'From_Dispatch_Date' },
    { headerName: 'To Dispatch Date', header: 'To Dispatch Date', exportHead: 'To Dispatch Date', field: 'To_Dispatch_Date' },
    { headerName: 'Selected Vehicle', header: 'Selected Vehicle', exportHead: 'Selected Vehicle', field: 'Selected_Vehicle' },
  ]

  const buildCriteriaData = (vehicleNo) => [
    {
      dataType: 'entry',
      From_Dispatch_Date: moment(fromDate).format('DD-MM-YYYY'),
      To_Dispatch_Date: moment(toDate).format('DD-MM-YYYY'),
      Selected_Vehicle: vehicleNo || '',
    },
  ]

  const formatDetailData = (table = []) => {
    const totals = {
      CargoCommission: 0,
      Units: 0,
      Paid: 0,
      ToPay: 0,
      Cartage: 0,
      Document: 0,
      Hamali: 0,
      TotalLrAmount: 0,
    }

    const rows = table.map((item, index) => {
      const row = {
        SN: index + 1,
        DispDate: item.DispatchDate ? moment(item.DispatchDate).format('DD-MM-YYYY hh:mm') : '',
        BusMasterNo: item.VehicleNo || '',
        DriverName: item.DriverConductorName || '',
        DriverNumber: item.ContactNo || '',
        Units: toNumber(item.Units),
        Paid: toNumber(item.Paid),
        ToPay: toNumber(item.ToPay),
        Cartage: toNumber(item.CartageAmount),
        Document: toNumber(item.DocumentCharges),
        Hamali: toNumber(item.Hamali),
        CargoCommission: toNumber(item.CargoCommission),
        TotalLrAmount: toNumber(item.TotalLrAmount),
        dataType: 'entry',
      }

      totals.CargoCommission += row.CargoCommission
      totals.Units += row.Units
      totals.Paid += row.Paid
      totals.ToPay += row.ToPay
      totals.Cartage += row.Cartage
      totals.Document += row.Document
      totals.Hamali += row.Hamali
      totals.TotalLrAmount += row.TotalLrAmount

      return row
    })

    if (rows.length > 0) {
      rows.push({
        SN: 'Total',
        dataType: 'total',
        colSpan: { SN: 5 },
        align: { SN: 'right' },
        ...Object.fromEntries(Object.entries(totals).map(([key, value]) => [key, round2(value)])),
      })
    }

    return rows
  }

  const summaryRedStyle = { color: '#ff0000', fontWeight: '700' }
  const summaryGreenStyle = { color: '#008000', fontWeight: '700' }
  const summaryBoldStyle = { fontWeight: '700' }

  const formatSummaryData = (summary = {}) => [
    {
      Summary: 'Total Freight Amount (Paid + ToPay)',
      Amount: round2(summary.TotalBooking),
      dataType: 'entry',
      cellStyle: { Summary: summaryBoldStyle, Amount: summaryBoldStyle },
    },
    {
      Summary: 'Less: ToPay Freight Amount',
      Amount: round2(summary.ToPay),
      dataType: 'entry',
      cellStyle: { Summary: summaryRedStyle, Amount: summaryRedStyle },
    },
    {
      Summary: 'Net Freight Amount (Paid)',
      Amount: round2(summary.NetBooking),
      dataType: 'entry',
      cellStyle: { Summary: summaryBoldStyle, Amount: summaryBoldStyle },
    },
    {
      Summary: 'Less: Commission on Total Freight Amount',
      Amount: round2(summary.Commission),
      dataType: 'entry',
      cellStyle: { Summary: summaryRedStyle, Amount: summaryRedStyle },
    },
    {
      Summary: 'Total Freight Amount after Commission Deduction',
      Amount: round2(summary.AfterCommission),
      dataType: 'entry',
      cellStyle: { Summary: summaryBoldStyle, Amount: summaryBoldStyle },
    },
    {
      Summary: 'Less: Hamali Charge',
      Amount: round2(summary.Hamali),
      dataType: 'entry',
      cellStyle: { Summary: summaryRedStyle, Amount: summaryRedStyle },
    },
    {
      Summary: 'Net Payable / Net Receivable',
      Amount: round2(summary.NetPayable),
      dataType: 'entry',
      cellStyle: { Summary: summaryBoldStyle, Amount: summaryGreenStyle },
      richText: {
        Summary: [
          { text: 'Net Payable', style: summaryRedStyle },
          { text: ' / ', style: summaryBoldStyle },
          { text: 'Net Receivable', style: summaryGreenStyle },
        ],
      },
    },
  ]

  const handleGo = () => {
    setLoader(true)
    setSelectedVehicle('')
    GetRequest(urls.getVehicleWiseVoucherData, getRequestBody())
      .then((res) => {
        if (res.status !== 'Success') {
          throw new Error(res?.ErrMessage || 'Request Failed')
        }
        const vehicles = (res?.Data?.Table || []).filter((item) => item?.VehicleNoFilter)
        if (vehicles.length === 0) {
          throw new Error('No Vehicle Found')
        }
        setVehicleData(vehicles)
        setSelectedVehicle(vehicles[0])
      })
      .catch((err) => {
        setVehicleData([])
        setAlert({ show: true, msg: err.message, nextId: '' })
      })
      .finally(() => setLoader(false))
  }

  const handleLoad = () => {
    if (!selectedVehicle || selectedVehicle === ' ') {
      setAlert({ show: true, msg: 'Please Select Vehicle', nextId: '' })
      return
    }
    const vehicleNo = selectedVehicle?.VehicleNoFilter || ''
    setLoader(true)
    GetRequest(urls.getVehicleWiseVoucherData, getRequestBody(vehicleNo))
      .then((res) => {
        if (res.status !== 'Success') {
          throw new Error(res?.ErrMessage || 'Request Failed')
        }
        const detailData = res?.Data?.Table1 || []
        const summaryData = res?.Data?.Table2 || []
        if (detailData.length === 0) {
          throw new Error('No Data Found')
        }

        openReportInNewTab({
          reportName: 'Vehicle Wise Voucher Report',
          mainHeader: vehicleWiseVoucherColumns,
          isMultiTable: true,
          tables: [
            {
              criteriaHeaders,
              criteriaData: buildCriteriaData(vehicleNo),
              mainHeader: vehicleWiseVoucherColumns,
              tableData: formatDetailData(detailData),
              colSpanMarker: 'DriverNumber',
            },
            { mainHeader: vehicleWiseVouchersummaryColumns, tableData: formatSummaryData(summaryData?.[0]) },
          ],
          isShowManageColumn: false,
          manageColumnID: '',
          showColumnBorders: true,
          isIncreFontSize: true,
        })
      })
      .catch((err) => {
        setAlert({ show: true, msg: err.message, nextId: '' })
      })
      .finally(() => setLoader(false))
  }
  return (
    <>
      <Item sx={{ padding: '0' }} className="reports">
        <ReportHeading reportName={reportName} />
        <Box sx={sx.reportFlex}>
          <Item sx={sx.itemsxGrow} style={{ width: '120px' }} className="input--container bookingDatePicker">
            <Datepicker
              name={'From_Dispatch_Date'}
              value={fromDate}
              label={'From Dispatch Date'}
              id={id.fromDate}
              dateChange={(date) => {
                setFromDate(date)
                // setShowTable(false)
                // focusElemnt(id.toDate)
              }}
              width="100%"
              dateDiff={730}
            />
          </Item>
          <Item sx={sx.itemsxGrow} style={{ width: '120px' }} className="input--container bookingDatePicker">
            <Datepicker
              label={'To Dispatch Date'}
              value={toDate}
              id={id.toDate}
              dateChange={(date) => {
                setToDate(date)
                // setShowTable(false)
                // focusElemnt('goodGoBtn')
              }}
              width="100%"
              dateDiff={730}
            />
          </Item>
          <Item sx={sx.itemsx} className="disabled-wrapper">
            <Button
              id={goButtonId}
              // onKeyDown={(e) => navigation3(e, id.gbSourceCity, id.toDate, () => filterData())}
              variant="contained"
              style={{ ...sx.fullHeight, ...sx.novp }}
              className="btn btn-primary"
              onClick={handleGo}
            >
              GO
            </Button>
          </Item>
          <Item sx={sx.itemsx} style={{ width: '230px' }}>
            <DropDownOA
              id={'SelectVehicleID'}
              label={'Select Vehicle'}
              searchPlaceholder={`Search Selected Vehicle`}
              defaultvalue={selectedVehicle?.VehicleNoFilter || selectedVehicle}
              data={vehicleData || []}
              listWidth="230px"
              dataKey={`VehicleNoFilter`}
              idKey={`VehicleNoFilter`}
              // disabled={user}
              onSelect={(obj) => setSelectedVehicle(obj)}
              nextId={loadButtonId}
              prevId={goButtonId}
              width="100%"
            />
          </Item>
          <Item sx={sx.itemsx} className="disabled-wrapper">
            <Button
              id={loadButtonId}
              variant="contained"
              style={{ ...sx.fullHeight, ...sx.novp }}
              className="btn btn-primary"
              onClick={handleLoad}
            >
              LOAD
            </Button>
          </Item>
        </Box>
      </Item>
    </>
  )
}

export default VehicleWiseVoucher
